import getStandingsMetrics from "@app/analytics/MeetingReport/utils/getStandingsMetrics";
import { QueryMetric } from "@app/analytics/meetops/types";
import { SAMPLE_MEETING_IDS } from "@app/analytics/sampleData/sampleMeetings";
import useAuth from "@app/common/auth/useAuth";

import useMeetingReport from "./useMeetingReport";
import useMeetingReportMetricsQuery from "./useMeetingReportMetricsQuery";
import { getDateRange } from "../utils";

const NO_USER_STANDINGS_METRICS: QueryMetric[] = [
  "avgParticipantTimeInMeeting",
  "cameraOffPercent",
  "microphoneOffPercent",
  "participantMeetingScore",
  "participantSentiment",
  "participantEngagement",
  "participantCharisma",
];

const STANDINGS_METRICS: QueryMetric[] = [
  ...NO_USER_STANDINGS_METRICS,
  "participantBias",
];

const DEFAULT_OVERVIEW_STATE = {
  dateRanges: [],
  error: false,
  loading: false,
  meetingDuration: 0,
  metrics: [],
};

const useMeetingReportStandings = (sessionId: string) => {
  const { user } = useAuth();

  const isSampleReport = SAMPLE_MEETING_IDS.includes(sessionId);

  const {
    meetingReport,
    isError: meetingReportQueryError,
    isLoading: meetingReportIsInitialLoading,
  } = useMeetingReport();

  const dateRanges = getDateRange(meetingReport?.rawStartTime);

  const {
    isLoading: everyoneMetricsIsInitialLoading,
    isError: everyoneMetricsQueryError,
    data: everyoneMetricsData,
  } = useMeetingReportMetricsQuery(
    {
      // don't fetch bias if there's no user
      metrics: user ? STANDINGS_METRICS : NO_USER_STANDINGS_METRICS,
      people: "everyone_but_me",
    },
    {
      queryKey: ["standings", "everyone", sessionId, user?.id],
      enabled: !isSampleReport,
    }
  );

  const {
    isLoading: myMetricsIsInitialLoading,
    isError: myMetricsQueryError,
    data: myMetricsData,
  } = useMeetingReportMetricsQuery(
    {
      metrics: STANDINGS_METRICS,
      people: "only_me",
    },
    {
      queryKey: ["standings", "me", sessionId, user?.id],
      enabled: Boolean(user) && !isSampleReport,
    }
  );

  const error =
    meetingReportQueryError || everyoneMetricsQueryError || myMetricsQueryError;

  const loading =
    meetingReportIsInitialLoading ||
    everyoneMetricsIsInitialLoading ||
    myMetricsIsInitialLoading;

  if (!sessionId || !meetingReport || loading) {
    return { ...DEFAULT_OVERVIEW_STATE, loading };
  }

  const allMetricsData = getStandingsMetrics(sessionId, {
    myMetrics: myMetricsData,
    everyoneMetrics: everyoneMetricsData,
  });

  return {
    dateRanges,
    error,
    loading,
    meetingReport,
    metrics: allMetricsData,
  };
};

export default useMeetingReportStandings;
